<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import { useMessagesStore } from '@/stores/messages'
import { dynamicLabel } from '@/stores/breadcrumb'
import Sidebar from './Sidebar.vue'
import PasswordWarningBanner from './PasswordWarningBanner.vue'
import QuickLinksBar from './QuickLinksBar.vue'

const route = useRoute()
const authStore = useAuthStore()
const dashboardStore = useDashboardStore()
const messagesStore = useMessagesStore()

const isMyDashboardEditPage = computed(() => route.name === 'my-dashboard-edit')
const isMessagesPage = computed(() => route.name === 'rh-messages')

interface BreadcrumbItem {
  label: string
  path?: string
}

const titles: Record<string, string> = {
  'dashboard': 'Dashboard',
  'components-showcase': 'Composants',
  'lots-test': 'Test Lots',
  'pdf-viewer-test': 'Visualiseur PDF',
  'rh-utilisateurs': 'Utilisateurs',
  'rh-utilisateur-creer': 'Créer un utilisateur',
  'rh-utilisateur-detail': 'Détail utilisateur',
  'rh-messages': 'Messages',
  'projets-opportunites-creer': 'Créer une opportunité',
  'projets-opportunites': 'Liste des opportunités',
  'projets-modeles': 'Liste des modèles',
  'projets-liste': 'Liste des projets',
  'projet-detail': 'Détail projet',
  'support-encours': 'Encours',
  'projets-suivi': 'Suivi projet',
  'admin-stats': 'Statistiques d\'usage',
  'societes-liste': 'Sociétés',
  'societes-contacts': 'Contacts',
  'facturation-suivi': 'Suivi Facturation',
  'facturation-archives': 'Archives',
  'facturation-echeanciers': 'Échéanciers',
  'support-historique': 'Historique',
  'support-gerer-projets': 'Gérer les projets',
  'support-parametres-devis': 'Paramètres des devis',
  'support-materiels': 'Liste des matériels',
  'support-laboratoires': 'Liste des laboratoires',
  'support-cellules': 'Liste des cellules',
  'documents': 'Documents',
  'om-creer': 'Créer un OM',
  'om-liste': 'Liste des OM',
  'om-validation': 'Validation des OM',
  'om-archives': 'Archives des OM',
  'my-dashboard': 'Mon Dashboard',
  'my-dashboard-creer': 'Créer un dashboard',
  'admin-widgets': 'Widgets',
  'admin-widget-creer': 'Créer un widget',
  'admin-widget-edit': 'Modifier le widget',
  'admin-quick-links': 'Liens rapides',
  'admin-notifications': 'Journal des notifications',
}

// Parent routes for breadcrumb
const parentRoutes: Record<string, { name: string; path: string }> = {
  'rh-utilisateur-detail': { name: 'rh-utilisateurs', path: '/rh/utilisateurs' },
  'projet-detail': { name: 'projets-liste', path: '/projets/liste' },
  'admin-widget-creer': { name: 'admin-widgets', path: '/admin/widgets' },
  'admin-widget-edit': { name: 'admin-widgets', path: '/admin/widgets' },
}

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const routeName = route.name as string
  const items: BreadcrumbItem[] = []

  // Check if current route has a parent
  const parent = parentRoutes[routeName]
  if (parent) {
    items.push({ label: titles[parent.name] || parent.name, path: parent.path })
  }

  // Add current page (use dynamic label if set, otherwise default)
  let currentLabel = dynamicLabel.value || titles[routeName] || 'Capatools'
  items.push({ label: currentLabel })

  return items
})
</script>

<template>
  <div class="main-layout">
    <Sidebar />
    <div class="layout-content">
      <header class="topbar">
        <!-- My Dashboard Edit: back button + title -->
        <div v-if="isMyDashboardEditPage" class="flex items-center gap-3">
          <router-link
            to="/my-dashboard"
            class="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </router-link>
          <span class="font-semibold text-gray-900 text-lg">
            {{ dashboardStore.currentDashboard?.name || 'Dashboard' }}
          </span>
        </div>

        <!-- Default breadcrumb -->
        <nav v-else class="breadcrumb">
          <template v-for="(item, index) in breadcrumbs" :key="index">
            <router-link v-if="item.path" :to="item.path" class="breadcrumb-link">
              {{ item.label }}
            </router-link>
            <span v-else class="breadcrumb-current" :title="item.label">{{ item.label }}</span>
            <span v-if="index < breadcrumbs.length - 1" class="breadcrumb-separator">›</span>
          </template>
        </nav>
        <div class="topbar-right">
          <!-- Bouton nouveau message (page messages) -->
          <template v-if="isMessagesPage">
            <button
              @click="messagesStore.openCreateModal()"
              class="topbar-btn topbar-btn--primary"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Nouveau message
            </button>
          </template>

          <QuickLinksBar />
          <!-- Boutons My Dashboard Edit -->
          <template v-if="isMyDashboardEditPage">
            <!-- Save indicator -->
            <span
              v-if="dashboardStore.isSaving"
              class="text-sm text-blue-600 flex items-center gap-2"
            >
              <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sauvegarde...
            </span>
            <span
              v-else-if="dashboardStore.hasUnsavedChanges"
              class="text-sm text-yellow-600"
            >
              Non sauvegarde
            </span>

            <button
              v-if="dashboardStore.isEditMode"
              @click="dashboardStore.openWidgetSelector()"
              class="topbar-btn topbar-btn--secondary"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Ajouter widget
            </button>
            <button
              @click="dashboardStore.toggleEditMode()"
              class="topbar-btn"
              :class="dashboardStore.isEditMode ? 'topbar-btn--primary' : 'topbar-btn--secondary'"
            >
              <svg v-if="!dashboardStore.isEditMode" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              {{ dashboardStore.isEditMode ? 'Terminer' : 'Modifier' }}
            </button>
          </template>


          <span v-if="authStore.user" class="topbar-user">
            Bienvenue <strong>{{ authStore.user.username || authStore.user.email }}</strong>
          </span>
        </div>
      </header>
      <PasswordWarningBanner />
      <main class="page-content">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.main-layout {
  @apply flex min-h-screen;
}

.layout-content {
  @apply flex-1 flex flex-col min-w-0;
  margin-left: 60px;
}

.topbar {
  @apply fixed top-0 right-0 z-40 flex h-16 shrink-0 items-center justify-between px-6 border-b border-gray-200 bg-white shadow-sm;
  left: 60px;
}

.breadcrumb {
  @apply flex items-center gap-2 text-lg;
}

.breadcrumb-link {
  @apply text-gray-500 hover:text-primary transition-colors;
}

.breadcrumb-current {
  @apply font-semibold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap max-w-[50ch];
}

.breadcrumb-separator {
  @apply text-gray-400;
}

.topbar-right {
  @apply flex items-center gap-3;
}

.topbar-user {
  @apply text-sm text-gray-700;
}

.topbar-btn {
  @apply px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors;
}

.topbar-btn--primary {
  @apply bg-primary text-white hover:bg-primary-dark;
}

.topbar-btn--secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200;
}

.page-content {
  @apply flex-1 bg-gray-100 flex flex-col overflow-hidden pt-16;
}
</style>
